import { alpha, Button, Chip, Stack, styled, Typography, useTheme } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import React, { useMemo } from 'react';
import { DynamicIcon, EllipsesMenu } from 'src/components';
import { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { getSendUnlessLabel } from 'src/pages/Conversations/Chat/InputField/hooks/use-send-unless-conditions';
import {
  getBannerActionSx,
  getComposerBannerStyles,
} from 'src/pages/Conversations/Chat/shared-styles';

type BannerVariant = 'default' | 'editing';

type BannerStatus = 'scheduled' | 'discarded';

interface ScheduledBannerProps {
  scheduledLabel: string;
  onView: () => void;
  onReschedule: () => void;
  onDiscard: () => void;
  isSaving?: boolean;
  conditions?: DraftScheduleSendUnless[] | null;
  isEditing?: boolean;
  onDoneEditing?: () => void;
  status?: BannerStatus;
  discardedLabel?: string;
  attachmentCount?: number;
}

const BannerContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'status',
})<{ variant: BannerVariant; status: BannerStatus }>(({ theme, variant, status }) => {
  const palette = theme.palette.custom?.OV?.sys;

  const tokensByState = {
    default: palette?.primary ?? theme.palette.info.main,
    editing: palette?.secondary ?? theme.palette.warning.main,
    discarded: palette?.onSurfaceVariant ?? theme.palette.text.primary,
  } as const;

  let token = tokensByState.default;

  if (status === 'discarded') {
    token = tokensByState.discarded;
  } else if (variant === 'editing') {
    token = tokensByState.editing;
  }

  return getComposerBannerStyles(theme, token);
});

const ActionsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ConditionsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

export const ScheduledBanner = ({
  scheduledLabel,
  onView,
  onReschedule,
  onDiscard,
  isSaving = false,
  conditions,
  isEditing = false,
  onDoneEditing,
  status = 'scheduled',
  discardedLabel,
  attachmentCount = 0,
}: ScheduledBannerProps): React.JSX.Element => {
  const theme = useTheme();
  const variant: BannerVariant = isEditing ? 'editing' : 'default';

  let leadingIcon = '';
  if (isEditing) {
    leadingIcon = 'file-pen';
  } else if (status === 'discarded') {
    leadingIcon = 'circle-minus';
  }

  let primaryLabel = '';
  if (isEditing) {
    if (status === 'discarded') {
      primaryLabel = 'Editing unsent message';
    } else {
      primaryLabel = 'Editing scheduled message';
    }
  } else if (status === 'discarded') {
    primaryLabel = 'Message not sent';
  }

  const uniqueConditionLabels = useMemo(() => {
    if (!conditions?.length) return [];
    const seen = new Set<string>();
    return conditions
      .map((c) => getSendUnlessLabel(c.type))
      .filter((label) => {
        if (seen.has(label)) return false;
        seen.add(label);
        return true;
      });
  }, [conditions]);

  const showScheduledLine = Boolean(scheduledLabel && (isEditing || status === 'scheduled'));
  const showHeaderRow = Boolean(primaryLabel || isSaving || leadingIcon);
  const showDiscardedMeta = status === 'discarded' && !isEditing;
  const hasConditions = uniqueConditionLabels.length > 0;

  return (
    <BannerContainer direction="row" variant={variant} status={status}>
      <Stack spacing={showHeaderRow ? 0.75 : 0} alignItems="flex-start">
        {showHeaderRow ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {!!leadingIcon && <DynamicIcon name={leadingIcon} library="lucide" />}
            {!!primaryLabel && (
              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'pre-wrap' }}>
                {primaryLabel}
              </Typography>
            )}
            {isSaving ? (
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Saving…
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {showScheduledLine ? (
          <Stack direction="row" spacing={0} margin={0} gap={0.625} alignItems="center">
            <DynamicIcon name="calendar-clock" library="lucide" sx={{ opacity: 0.8 }} />
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', opacity: 0.9 }}>
              Scheduled for {scheduledLabel}
            </Typography>
            {attachmentCount > 0 ? (
              <Stack
                direction="row"
                gap="2px"
                alignItems="center"
                sx={{ opacity: 0.9 }}
                title={`${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`}
              >
                <DynamicIcon name="paperclip" library="lucide" sx={{ fontSize: '1rem' }} />
                <Typography variant="body2">{attachmentCount}</Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {showDiscardedMeta ? (
          <Stack spacing={0.5}>
            {scheduledLabel ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.85 }}>
                <DynamicIcon name="calendar-clock" library="lucide" />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  Was scheduled for {scheduledLabel}
                </Typography>
              </Stack>
            ) : null}
            {discardedLabel ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.85 }}>
                <DynamicIcon name="history" library="lucide" />
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  Cancelled on {discardedLabel}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        ) : null}

        {hasConditions ? (
          <ConditionsRow>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {status === 'discarded' ? 'Canceled due to conditions:' : 'Cancel send if:'}
            </Typography>
            {uniqueConditionLabels.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.common.black, 0.08),
                  color: 'inherit',
                }}
              />
            ))}
          </ConditionsRow>
        ) : null}
      </Stack>

      <ActionsContainer>
        {isEditing ? (
          <Button
            variant="contained"
            color="inherit"
            onClick={onDoneEditing ?? onView}
            sx={{
              ...getBannerActionSx(theme),
              color: 'inherit',
              backgroundColor: alpha(theme.palette.common.white, 0.18),
              '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.28) },
            }}
          >
            Done
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onView}
            sx={getBannerActionSx(theme)}
          >
            View message
          </Button>
        )}
        <V2ThemeProvider>
          <EllipsesMenu
            options={[
              {
                label: 'Reschedule Message',
                onClick: onReschedule,
                icon: 'calendar-clock',
                iconLibrary: 'lucide',
              },
              {
                label: status === 'discarded' ? 'Remove message' : 'Cancel schedule',
                onClick: onDiscard,
                icon: 'trash-2',
                iconLibrary: 'lucide',
                danger: true,
              },
            ]}
          />
        </V2ThemeProvider>
      </ActionsContainer>
    </BannerContainer>
  );
};

export default ScheduledBanner;
